import * as React from 'react';
import { FlatViewOrderSinglePageDesktop, ICallbackParams } from '@cian/newbuilding-flat-view-order-component';
import { Button } from '@cian/ui-kit';
import { IconProductDateTime16 } from '@cian/ui-kit-design-tokens/icons';
import { useSelector, useDispatch } from 'react-redux';
import { trackFlatViewOrderPopupOpen, trackFlatViewOrderSuccess } from './tracking';
import { saveLegalAgreements } from './utils/saveLegalAgreements';
// oxlint-disable-next-line cian-eslint-compat/no-restricted-paths
import { addFlatTourBooking } from '../../../browser/services/flatTourBooking';
import { getFlatTourBookingSchedule, getIsFlatTourBookingAvailable } from '../../selectors/flatTourBooking';
import { getDisplayName, getIsFlatViewOrderEnabled, getNewbuildingId } from '../../selectors/newbuilding';
import { TThunkDispatch } from '../../types/redux/thunk';
import { useApplicationContext } from '../../utils/withApplicationContext';

export function FlatViewOrderPopupContainer() {
  const [isPopupOpened, setPopupVisibility] = React.useState(false);
  const dispatch: TThunkDispatch = useDispatch();
  const isFlatTourBookingAvailable = useSelector(getIsFlatTourBookingAvailable);
  const isEnabled = useSelector(getIsFlatViewOrderEnabled);
  const newbuildingName = useSelector(getDisplayName);
  const newbuildingId = useSelector(getNewbuildingId);
  const schedule = useSelector(getFlatTourBookingSchedule);

  const { config, httpApi, logger } = useApplicationContext();

  const privacyPolicyUrl = config.get<string>('callback-widget.privacyPolicyUrl') || undefined;
  const marketingAgreementUrl = config.get<string>('callback-widget.marketingAgreementUrl') || undefined;
  const marketingAgreementVersion = config.get<number>('callback-widget.marketingAgreementVersion') || 1;
  const personalDataAgreementUrl = config.get<string>('callback-widget.personalDataAgreementUrl') || undefined;
  const personalDataAgreementVersion = config.get<number>('callback-widget.personalDataAgreementVersion') || 1;
  const isMarketingAgreementEnabled = config.get<boolean>('Newbuilding.Core.MarketingAgreement.Enabled') || false;

  const onButtonClick = React.useCallback(() => {
    trackFlatViewOrderPopupOpen('personal_tour_blockJK');

    setPopupVisibility(true);
  }, [setPopupVisibility]);

  const onSuccess = React.useCallback((params: ICallbackParams) => {
    trackFlatViewOrderSuccess(params);
  }, []);

  const onSubmit = React.useCallback(
    async (params: ICallbackParams) => {
      try {
        const parameters = {
          phone: params.phone,
          tourTime: `${params.date} ${params.time}`,
          fullName: params.name,
          newbuildingId,
        };

        await dispatch(addFlatTourBooking(parameters));

        if (params.acceptedAgreements && params.acceptedAgreements.length > 0) {
          saveLegalAgreements({
            httpApi,
            logger,
            acceptedAgreements: params.acceptedAgreements,
            marketingAgreementVersion,
            personalDataAgreementVersion,
            source: 'newbuilding-card-desktop-fichering-frontend.flat-view-order',
          });
        }

        return true;
      } catch {
        return false;
      }
    },
    [dispatch],
  );

  const onPopupClose = React.useCallback(() => {
    setPopupVisibility(false);
  }, []);

  if (!isEnabled) {
    return null;
  }

  return (
    <>
      <Button
        theme="stroke_primary"
        size="M"
        onClick={onButtonClick}
        disabled={!isFlatTourBookingAvailable}
        beforeIcon={<IconProductDateTime16 color="current_color" />}
      >
        Записаться на просмотр
      </Button>
      {schedule && (
        <FlatViewOrderSinglePageDesktop
          isOpen={isPopupOpened}
          onClose={onPopupClose}
          onSuccess={onSuccess}
          onSubmit={onSubmit}
          disabledDays={schedule.disabledDays}
          availableDates={schedule.availableDates}
          newbuildingName={newbuildingName}
          minDate={schedule.minDate}
          maxDate={schedule.maxDate}
          nextDate={schedule.nextDate}
          nextNextDate={schedule.nextNextDate}
          privacyPolicyUrl={privacyPolicyUrl}
          marketingAgreementUrl={marketingAgreementUrl}
          personalDataAgreementUrl={personalDataAgreementUrl}
          isMarketingAgreementEnabled={isMarketingAgreementEnabled}
        />
      )}
    </>
  );
}
